import type { KeyboardEvent } from "react"

import { useTranslation } from "react-i18next"

import { Eye } from "@/components/icons"
import { Button } from "@/components/ui/button"
import fileViewerStyles from "@/features/file-viewer/styles/file-viewer.module.css"
import { cn } from "@/lib/utils"
import { normalizeForMatching } from "@/utils/normalize-for-matching"

const SNIPPET_MAX_LENGTH = 200

type CitationPeekContentProps = {
  title: string
  pageNumber: number | null
  chunkText?: string | null
  quoteText?: string | null
  onViewDocument: () => void
  interactionMode?: "button" | "card" | "static"
  expanded?: boolean
  hidePreviewText?: boolean
}

type ContextPreview = {
  before: string
  focus: string
  after: string
  hasPrefix: boolean
  hasSuffix: boolean
}

function buildContextPreview(
  sourceText: string,
  focusText: string,
  radius: number
): ContextPreview | null {
  const source = normalizeForMatching(sourceText)
  const focus = normalizeForMatching(focusText)
  if (!source || !focus) {
    return null
  }

  const sourceLower = source.toLowerCase()
  const focusLower = focus.toLowerCase()
  const matchIndex = sourceLower.indexOf(focusLower)
  if (matchIndex < 0) {
    return null
  }

  const matchEnd = matchIndex + focus.length
  const excerptStart = Math.max(0, matchIndex - radius)
  const excerptEnd = Math.min(source.length, matchEnd + radius)

  return {
    before: source.slice(excerptStart, matchIndex),
    focus: source.slice(matchIndex, matchEnd),
    after: source.slice(matchEnd, excerptEnd),
    hasPrefix: excerptStart > 0,
    hasSuffix: excerptEnd < source.length,
  }
}

function getPreviewText(
  chunkText: string | null | undefined,
  quoteText: string | null | undefined,
  expanded: boolean
): {
  context: ContextPreview | null
  fallbackText: string | null
  fallbackTruncated: boolean
  fallbackFromQuote: boolean
} {
  const contextRadius = expanded ? 700 : 90
  const fallbackMax = expanded ? 1400 : SNIPPET_MAX_LENGTH

  const normalizedChunk = chunkText ? normalizeForMatching(chunkText) : ""
  const normalizedQuote = quoteText ? normalizeForMatching(quoteText) : ""

  const context =
    normalizedChunk && normalizedQuote
      ? buildContextPreview(normalizedChunk, normalizedQuote, contextRadius)
      : null
  if (context) {
    return {
      context,
      fallbackText: null,
      fallbackTruncated: false,
      fallbackFromQuote: false,
    }
  }

  const base = normalizedQuote || normalizedChunk
  const fallbackFromQuote = !!normalizedQuote
  if (!base) {
    return {
      context: null,
      fallbackText: null,
      fallbackTruncated: false,
      fallbackFromQuote: false,
    }
  }

  if (base.length <= fallbackMax) {
    return {
      context: null,
      fallbackText: base,
      fallbackTruncated: false,
      fallbackFromQuote,
    }
  }

  return {
    context: null,
    fallbackText: base.slice(0, fallbackMax).trim(),
    fallbackTruncated: true,
    fallbackFromQuote,
  }
}

export function CitationPeekContent({
  title,
  pageNumber,
  chunkText,
  quoteText,
  onViewDocument,
  interactionMode = "button",
  expanded = false,
  hidePreviewText = false,
}: CitationPeekContentProps) {
  const { t } = useTranslation()
  const preview = getPreviewText(chunkText, quoteText, expanded)

  const handleContainerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (interactionMode !== "card") {
      return
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      onViewDocument()
    }
  }

  return (
    <div
      className={cn("space-y-3", interactionMode === "card" && "cursor-pointer")}
      role={interactionMode === "card" ? "button" : undefined}
      tabIndex={interactionMode === "card" ? 0 : undefined}
      onClick={interactionMode === "card" ? onViewDocument : undefined}
      onKeyDown={handleContainerKeyDown}
    >
      <div>
        <p className="text-sm leading-tight font-semibold">{title}</p>
        {pageNumber != null && (
          <p className="text-muted-foreground text-xs">
            {t("answer.page", { defaultValue: "Page" })} {pageNumber}
          </p>
        )}
      </div>
      {!hidePreviewText && (
        <div
          className={cn(
            fileViewerStyles.citationHighlightScope,
            "bg-muted/20 rounded-md border p-2"
          )}
        >
          {preview.context && (
            <p
              className={cn(
                "text-muted-foreground text-xs leading-relaxed",
                expanded ? "line-clamp-none" : "line-clamp-4"
              )}
            >
              {preview.context.hasPrefix && "…"}
              {preview.context.before}
              <mark className="citation-highlight">{preview.context.focus}</mark>
              {preview.context.after}
              {preview.context.hasSuffix && "…"}
            </p>
          )}
          {!preview.context && preview.fallbackText && (
            <p
              className={cn(
                "text-muted-foreground text-xs leading-relaxed",
                expanded ? "line-clamp-none" : "line-clamp-4"
              )}
            >
              {preview.fallbackFromQuote ? (
                <mark className="citation-highlight">{preview.fallbackText}</mark>
              ) : (
                preview.fallbackText
              )}
              {preview.fallbackTruncated && "…"}
            </p>
          )}
        </div>
      )}
      {interactionMode === "button" && (
        <Button className="w-full gap-2" size="sm" variant="secondary" onClick={onViewDocument}>
          <Eye className="size-3" />
          {t("answer.view_in_document")}
        </Button>
      )}
    </div>
  )
}
